import styled from 'styled-components';
import _ from 'lodash';
import type { ColDef, SortModelItem } from 'ag-grid-community';
import DataTable, { useIsDataTable } from 'components/DataTable';
import DataTableRenderers from 'components/DataTable/DataTable.renderers';
import { ShimSortModel } from 'components/DataTable/agParamsShim';
import toDataTableSizing from 'components/DataTable/toDataTableSizing';
import useInfiniteRows from 'components/DataTable/useInfiniteRows';
import StyleOverrides, {
  GRID_BORDERS_CSS,
} from 'components/Grid/StyleOverrides';
import { AgGridReact } from 'ag-grid-react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import buildGridOptions from '../../../Grid/buildGridOptions';
import { RowHeightContext } from '../../../../contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import { GridOptions } from 'ag-grid-community';
import Typography from '../../../../kingpin/atoms/Typography';
import useDatasource from '../../../../hooks/grids/useDatasource';
import useFilterInput from '../../../../hooks/useFilterInput';
import GridFrameworkComponents from '../../../Grid/GridFrameworkComponents';
import GridInputs from './GridInputs';
import { BonusRecordTaggingContext } from './BonusRecordTaggingProvider';
import {
  GridSelectedDocumentsContext,
  ROW_SELECTION_OPTIONS,
} from '../../../Grid/SelectDocumentCell';
import useFilteredColumnDefs from './useFilteredColumnDefs';
import { PERIOD_LOCKED_MESSAGE } from './tagConstants';

const GridWrapperDiv = styled(StyleOverrides)`
  height: 100%;
  width: 100%;
  padding-top: 12px;
`;

const GridInnerDiv = styled.div`
  height: 100%;
  width: 100%;
  font-size: 11px;
  ${GRID_BORDERS_CSS}
`;

const getRecordId = (data: DataTable.Row) => `${data.id}`;

// AG's server-side grid over the period's records
const AgRecordTaggingGrid = ({
  gridConfig,
  fields,
  filterInput,
  rowEnricher,
  columnDefs,
  gridOptions,
}: {
  gridConfig: BonusRecordTagging.RecordTaggingConfig;
  fields: string[];
  filterInput: FilterInput[];
  rowEnricher: (elasticDocument: ElasticDocument) => ElasticDocument;
  columnDefs: ColDef[];
  gridOptions: GridOptions;
}) => {
  const { dataSource } = useDatasource(
    fields,
    gridConfig.dataType,
    filterInput,
    rowEnricher,
  );

  if (!dataSource) {
    return null;
  }

  return (
    <GridInnerDiv className="ag-theme-balham ag-theme-fleetops">
      <AgGridReact
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        serverSideDatasource={dataSource}
        components={GridFrameworkComponents}
      ></AgGridReact>
    </GridInnerDiv>
  );
};

// The period's records drawn by DataTable, a page at a time, sized as the
// config's column size type asks (fit to their cells by default)
const RecordTaggingTable = ({
  gridConfig,
  fields,
  filterInput,
  rowEnricher,
  columnDefs,
  gridOptions,
}: {
  gridConfig: BonusRecordTagging.RecordTaggingConfig;
  fields: string[];
  filterInput: FilterInput[];
  rowEnricher: (elasticDocument: ElasticDocument) => ElasticDocument;
  columnDefs: ColDef[];
  gridOptions: GridOptions;
}) => {
  const [sortModel, setSortModel] = useState<SortModelItem[]>([]);
  const onSortChanged = useCallback(
    (next: ShimSortModel) =>
      setSortModel((current) => (_.isEqual(current, next) ? current : next)),
    [],
  );
  const getColDef = useCallback(
    (colId: string) => columnDefs.find((c) => (c.colId ?? c.field) === colId),
    [columnDefs],
  );
  const { rows, totalCount, isLoadingFirstPage, fetchNextPage } =
    useInfiniteRows({
      dataType: gridConfig.dataType,
      includeFields: fields,
      filterInput,
      sortModel,
      getColDef,
      rowEnricher,
    });
  const sizeType = gridConfig.columnSizeStrategy?.type ?? 'fitCellContents';
  const {
    sizing,
    columnDefs: sizedColumnDefs,
    fittedColumnIds,
    skipHeaderWhenSizing,
  } = useMemo(
    () =>
      toDataTableSizing({
        strategy:
          sizeType === 'fitGridWidth'
            ? { type: 'fitGridWidth', columnLimits: [] }
            : { type: 'fitCellContents', skipHeader: false },
        colDefs: columnDefs,
      }),
    [columnDefs, sizeType],
  );

  return (
    <GridInnerDiv>
      <DataTable
        testId="record-tagging-grid"
        columnDefs={sizedColumnDefs}
        defaultColDef={gridOptions.defaultColDef}
        components={DataTableRenderers}
        rows={rows}
        gridOptions={gridOptions}
        sizing={sizing}
        fittedColumnIds={fittedColumnIds}
        skipHeaderWhenSizing={skipHeaderWhenSizing}
        isManualSorting
        onSortChanged={onSortChanged}
        isVirtualised
        onEndReached={fetchNextPage}
        isLoading={isLoadingFirstPage}
        noDataToDisplay={!isLoadingFirstPage && rows.length === 0}
        totalRowCount={totalCount}
        getRowId={getRecordId}
        isResizable
        hasBorders
      />
    </GridInnerDiv>
  );
};

const RecordTaggingGrid = ({
  gridConfig,
  fields,
  filterInput,
}: {
  gridConfig: BonusRecordTagging.RecordTaggingConfig;
  fields: string[];
  filterInput: FilterInput[];
}) => {
  const { rowHeight } = useContext(RowHeightContext);
  const isDataTable = useIsDataTable('recordTagging');
  const {
    isShowingAllFields,
    selectedDocumentIds,
    setSelectedDocumentIds,
    setTaggedDocumentIds,
    dateScopeOverride,
    configuredTag,
    isReadOnly,
  } = useContext(BonusRecordTaggingContext);
  // We have no GET endpoint for tagged records, so the currently-tagged set is
  // derived from the `tags` field on each row as pages stream in: remember the
  // id as persisted-tagged and pre-select it (first time only). The pill itself
  // is rendered reactively from this set by BonusTagPillCell.
  const rowEnricher = useCallback(
    (elasticDocument: ElasticDocument): ElasticDocument => {
      if (
        'id' in elasticDocument &&
        Array.isArray(elasticDocument.tags) &&
        elasticDocument.tags.includes(configuredTag)
      ) {
        const id = elasticDocument.id;
        setTaggedDocumentIds((prev) =>
          prev.includes(id) ? prev : [...prev, id],
        );
        setSelectedDocumentIds((prev) =>
          prev.includes(id) ? prev : [...prev, id],
        );
      }

      return elasticDocument;
    },
    [configuredTag, setTaggedDocumentIds, setSelectedDocumentIds],
  );
  const columnDefs = useFilteredColumnDefs(gridConfig, isShowingAllFields);

  const gridOptions = useMemo((): GridOptions => {
    return {
      ...buildGridOptions({ rowHeight }),
      autoSizeStrategy: {
        type: gridConfig.columnSizeStrategy?.type || 'fitCellContents',
      },
      suppressColumnVirtualisation: true,
      ...ROW_SELECTION_OPTIONS,
    };
  }, [gridConfig, rowHeight]);

  if (!dateScopeOverride?.startDate || !dateScopeOverride?.endDate) {
    return null;
  }

  return (
    <>
      <div style={{ paddingTop: 24 }}>
        <Typography.Header type={'H3'}>
          <span>Data Records</span>
        </Typography.Header>
      </div>

      <GridInputs />
      <GridWrapperDiv>
        <GridSelectedDocumentsContext.Provider
          value={{
            selectedDocumentIds,
            setSelectedDocumentIds,
            isDisabled: isReadOnly,
            disabledMessage: PERIOD_LOCKED_MESSAGE,
          }}
        >
          {isDataTable ? (
            <RecordTaggingTable
              gridConfig={gridConfig}
              fields={fields}
              filterInput={filterInput}
              rowEnricher={rowEnricher}
              columnDefs={columnDefs}
              gridOptions={gridOptions}
            />
          ) : (
            <AgRecordTaggingGrid
              gridConfig={gridConfig}
              fields={fields}
              filterInput={filterInput}
              rowEnricher={rowEnricher}
              columnDefs={columnDefs}
              gridOptions={gridOptions}
            />
          )}
        </GridSelectedDocumentsContext.Provider>
      </GridWrapperDiv>
    </>
  );
};

const Gate = ({
  gridConfig,
}: {
  gridConfig: BonusRecordTagging.RecordTaggingConfig;
}) => {
  const [fields, setFields] = useState<string[] | undefined>();
  const [listFi, setListFi] = useState<FilterInput[] | undefined>();
  const { filterInput } = useFilterInput({});
  const { isShowingAllFields } = useContext(BonusRecordTaggingContext);

  useEffect(() => {
    // 'tags' is always fetched (never a display column) so the pill and the
    // derived tagged-state can read each record's current tags.
    if (isShowingAllFields) {
      const f = gridConfig.fields.map((f) => f.field);
      setFields(['id', 'tags', ...f]);
    } else {
      const f = gridConfig.fields
        .filter((f) => f.isDefault)
        .map((f) => f.field);
      setFields(['id', 'tags', ...f]);
    }

    setListFi([filterInput]);
  }, [filterInput, gridConfig.fields, isShowingAllFields]);

  if (!fields || !listFi) {
    return null;
  }

  return (
    <RecordTaggingGrid
      gridConfig={gridConfig}
      fields={fields}
      filterInput={listFi}
    />
  );
};
export default Gate;
